/**
 * Hook for fetching historical story events
 * Story 234: Real-time MCP Updates Stream
 *
 * Fetches events from the REST API for historical data.
 */

import { useQuery } from '@tanstack/react-query';
import { config } from '../config';
import type { McpStreamEvent } from './useUpdatesStream';

/** API response shape */
interface EventsResponse {
  events: McpStreamEvent[];
}

/** Query key factory for story events */
export const storyEventsKeys = {
  all: ['story-events'] as const,
  list: (projectSlug: string, storyId: string) =>
    [...storyEventsKeys.all, projectSlug, storyId] as const,
};

/**
 * Fetch historical events for a story
 */
async function fetchStoryEvents(
  projectSlug: string,
  storyId: string
): Promise<McpStreamEvent[]> {
  const encodedSlug = encodeURIComponent(projectSlug);
  const response = await fetch(
    `${config.apiUrl}/api/v1/projects/${encodedSlug}/stories/${storyId}/events`,
    {
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch events: ${response.status}`);
  }

  const data: EventsResponse = await response.json();
  return data.events;
}

/**
 * Hook for fetching historical events for a story
 *
 * @param projectSlug - Project slug (owner/repo)
 * @param storyId - Story external ID
 * @returns Query result with historical events
 */
export function useStoryEvents(projectSlug: string, storyId: string | null) {
  return useQuery({
    queryKey: storyEventsKeys.list(projectSlug, storyId ?? ''),
    queryFn: () => fetchStoryEvents(projectSlug, storyId!),
    enabled: !!storyId,
    staleTime: 60 * 1000, // Consider data fresh for 1 minute
    refetchOnWindowFocus: false, // Rely on real-time updates instead
  });
}
